/*
 * Tracking-consent posture for the popup engine.
 *
 * Our embed runs on the customer's site, so we never ship a banner of our own:
 * two banners on one page is the worst outcome available, and their consent
 * tool is the one their visitor answered. What we do instead is read whatever
 * that tool published, behind the interface below.
 *
 * The three shapes a consent tool publishes in (CLAUDE.md §13): a cookie, a
 * `localStorage` key, or a value the page exposes on `window`, sometimes as a
 * function. All three are configured by name, so adding support for another
 * tool is a settings change rather than a code change.
 *
 * Configured by name is also their limit, and it is why there is a fourth
 * source. The customer has to know their tool's cookie name and the category
 * string inside it, which is a developer task on a product for non-developers
 * (§0a), and no two consent tools spell any of it the same way. Google Consent
 * Mode is the one vocabulary they share, because it has been mandatory for EEA
 * advertisers since March 2024, so it is read by shape rather than by name and
 * the customer supplies nothing. That is the single place a vendor appears in
 * this file, and it earns the exception by being the interoperability layer the
 * others publish through.
 *
 * What the answer governs is one layer only, per decision 0.2 of
 * docs/consent-management_plan.md. Opening the popup is never gated, because a
 * page reacting to a pointer sends nothing anywhere. The `kiroku:popup:*`
 * frequency cap is never gated, because it exists to show people fewer popups
 * and it never leaves the device. The analytics beacon (`popup_shown`,
 * `popup_converted`, `popup_declined`, which reach us carrying an IP) is the
 * layer this file exists for.
 *
 * With no signal to read, tracking runs: see DEFAULT_TRACKING_POSTURE, where
 * the reasoning and its cost are written down. Nothing here throws, stores
 * anything, or makes a network request, so it cannot delay first paint.
 *
 * Related but different, and not to be conflated: the form-submission consent
 * checkbox in the marketing templates, and AI/MCP consent in
 * `src/lib/mcp/consent.ts`.
 */

/** What the host page's consent signal says about behavioural tracking. */
export type TrackingConsentState = "granted" | "denied" | "unknown";

/** Which configured source answered. */
export type TrackingConsentSource =
  | "window"
  | "consentMode"
  | "cookie"
  | "storage";

/**
 * What happens when no signal can be read: tracking runs.
 *
 * This is a decision and it is written here so it can be read, not a default
 * that fell out of the code (CLAUDE.md §13). Most small-business sites have no
 * consent tool at all, so blocking on an absent signal would leave the majority
 * of customers with popups that count nothing, no error, and nothing in the
 * dashboard explaining why. The cost of choosing this way is real: a visitor on
 * a site with no consent tool is counted without having been asked, and the
 * count carries their IP. That is the customer's disclosure to make on their
 * own site, and `/privacy` describes what the embed observes.
 */
export const DEFAULT_TRACKING_POSTURE: "allow" | "block" = "allow";

/**
 * Where to read the signal, and how to read the answer. Every field is
 * optional; a config with no source configured resolves to "unknown", which is
 * the state of every site that has no consent tool.
 */
export interface TrackingConsentConfig {
  /**
   * Dotted path to a value on the host page's `window`, for example
   * `"myCmp.consent.marketing"`. A function at the end of the path is called
   * with no arguments and its return value is read. Consulted first, because a
   * live value reflects a choice the visitor changed a moment ago and a cookie
   * may not.
   */
  windowPath?: string;
  /**
   * Read Google Consent Mode, which the host page publishes as `consent`
   * commands on `window.dataLayer`. `true` reads `analytics_storage`, the key
   * that governs measurement and the only one this product needs; a string
   * names another key, so the advertising keys (`ad_storage`, `ad_user_data`,
   * `ad_personalization`) are selectable without being the default.
   *
   * Unlike every other source this one needs no name from the customer, which
   * is the point of it: anybody running Google Tag Manager already has it, and
   * a consent tool that speaks Consent Mode publishes here whatever it calls
   * its own cookie.
   *
   * Consulted after `windowPath`, because a path the customer typed points at
   * one exact answer while the queue is shared with every other tag on the
   * page.
   */
  consentMode?: boolean | string;
  /** Cookie name, for example `"visitor_consent"`. */
  cookie?: string;
  /** `localStorage` key. */
  storageKey?: string;
  /**
   * Which category inside the value carries the answer, for consent tools that
   * publish several in one place. Handles an object (`{ads: false}`), the
   * loose `{necessary:true,ads:false}` shape written unquoted into cookies, and
   * a list of granted category ids (`["essential","measurement"]`), where being
   * in the list is granted and being absent is denied.
   */
  category?: string;
  /** Extra spellings that mean granted, compared case-insensitively. */
  grantedValues?: readonly string[];
  /** Extra spellings that mean denied, compared case-insensitively. */
  deniedValues?: readonly string[];
  /**
   * Read the browser's Global Privacy Control signal
   * (`navigator.globalPrivacyControl`) as well as any source above.
   *
   * This is a flag rather than a `windowPath` because GPC's polarity is
   * backwards from everything else here: it is `true` when the visitor has
   * opted **out**, while a truthy value from every other source means granted.
   * Naming it lets this module apply the inversion, so the customer cannot get
   * it the wrong way round.
   *
   * It only ever denies. GPC `false` means the visitor expressed no
   * preference, which is not a yes, so it leaves the question to the sources
   * above and then to `DEFAULT_TRACKING_POSTURE`. A denial wins over a banner
   * that granted: a visitor may accept a site's cookie banner and still send a
   * global opt-out, and the opt-out is the one the CCPA treats as binding.
   */
  gpc?: boolean;
}

/** Minimal read-only subset of the Web Storage API this module depends on. */
export interface ConsentStorageLike {
  getItem(key: string): string | null;
}

/**
 * Where to read from. Defaults to the ambient page. Injectable so a test, and
 * later the embed, can drive the lookup without touching real browser state.
 */
export interface TrackingConsentEnvironment {
  /** Raw `document.cookie`. Defaults to the ambient document's. */
  cookieString?: string | null;
  /** Storage backend. Defaults to `window.localStorage`. */
  storage?: ConsentStorageLike | null;
  /** Object `windowPath` is resolved against. Defaults to the ambient window. */
  scope?: Record<string, unknown> | null;
}

/**
 * Every sentence this module tells a form owner, in one place.
 *
 * These are written for somebody reading their dashboard, not for a developer
 * reading a log: CLAUDE.md §13 requires that a suppressed popup event says why,
 * and task 5.3 of docs/consent-management_plan.md renders them on the form
 * overview. Having one home for them is what stops the dashboard growing a
 * second, kinder set of words that no longer describes what the engine did.
 *
 * `revoked` belongs here for the same reason even though only the popup engine
 * can produce it: it is a suppression the owner has to be able to read about,
 * and it used to have a private copy in `popupTriggers.ts`.
 */
export const TRACKING_CONSENT_REASONS = {
  granted:
    "This visitor's site says tracking is allowed, so popup views and outcomes are counted.",
  deniedByBrowser:
    "This visitor's browser asks every site not to sell or share their data, so the popup still opens and nothing is counted.",
  deniedBySite:
    "This visitor's site says tracking is denied, so the popup still opens and nothing is counted.",
  unreadableCounted:
    "No answer could be read from the consent signal on this page, so popup views and outcomes are counted.",
  unreadableNotCounted:
    "No answer could be read from the consent signal on this page, so nothing is counted.",
  noSignalCounted:
    "This site has no consent signal set up, so popup views and outcomes are counted.",
  noSignalNotCounted:
    "This site has no consent signal set up, so nothing is counted.",
  revoked:
    "This visitor turned tracking off while the page was open, so nothing more is counted for them here.",
} as const;

export interface TrackingConsentResolution {
  /** What the signal said, or "unknown" when there was nothing to read. */
  state: TrackingConsentState;
  /** Which source answered; null when none did. */
  source: TrackingConsentSource | null;
  /** Whether the analytics layer may run, after applying the default posture. */
  allowed: boolean;
  /**
   * One plain sentence, written for a form owner reading the dashboard rather
   * than for a developer reading a log. §13 requires that a suppressed popup
   * event says why, and this is the text that answers it.
   */
  reason: string;
}

const GRANTED_TOKENS = [
  "true",
  "1",
  "yes",
  "y",
  "granted",
  "grant",
  "allow",
  "allowed",
  "accept",
  "accepted",
  "opt-in",
  "optin",
  "on",
];

const DENIED_TOKENS = [
  "false",
  "0",
  "no",
  "n",
  "denied",
  "deny",
  "disallow",
  "disallowed",
  "reject",
  "rejected",
  "declined",
  "opt-out",
  "optout",
  "off",
];

/** Guards against a cyclic object or a function that returns itself. */
const MAX_DEPTH = 4;

/**
 * The property name that carries the Global Privacy Control signal, wherever a
 * page hangs it. Matched on the last path segment so `navigator.
 * globalPrivacyControl` and a copy republished at `myCmp.globalPrivacyControl`
 * are both recognised.
 */
const GPC_PROPERTY = "globalprivacycontrol";

/** Where GPC lives when the `gpc` flag asks for it rather than a path. */
const GPC_WINDOW_PATH = "navigator.globalPrivacyControl";

/** Whether a configured `windowPath` points at the GPC signal. */
function isGpcPath(path: string): boolean {
  const segments = path.split(".").filter(Boolean);
  const last = segments[segments.length - 1] ?? "";
  return last.trim().toLowerCase() === GPC_PROPERTY;
}

/**
 * Apply GPC polarity to whatever the page published.
 *
 * The signal being on is an opt-out, so it maps to denied. The signal being
 * off maps to "unknown" and never to "granted": the spec says `false` means
 * the visitor has not enabled GPC, which says nothing about what they would
 * consent to, and reading it as a yes would be this same bug pointing the
 * other way.
 *
 * The customer's own `grantedValues`/`deniedValues` are deliberately not
 * consulted here. The polarity of this one signal is fixed by the spec, and
 * honouring a custom mapping would put the footgun back within reach.
 */
function interpretGpc(value: unknown): TrackingConsentState {
  return interpret(value, {}) === "granted" ? "denied" : "unknown";
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Read one cookie by name. Returns null when it is not set or is unreadable. */
function readCookie(name: string, cookieString: string | null): string | null {
  if (!cookieString) return null;
  for (const pair of cookieString.split(";")) {
    const separator = pair.indexOf("=");
    if (separator < 0) continue;
    if (pair.slice(0, separator).trim() !== name) continue;
    const raw = pair.slice(separator + 1).trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw; // a value that is not percent-encoded is still a value
    }
  }
  return null;
}

/** Walk a dotted path, calling a function found at the end of it. */
function readWindowPath(
  path: string,
  scope: Record<string, unknown> | null,
): unknown {
  if (!scope) return undefined;
  const segments = path.split(".").filter(Boolean);
  if (!segments.length) return undefined;

  let parent: unknown = null;
  let current: unknown = scope;
  for (const segment of segments) {
    if (current === null || typeof current === "undefined") return undefined;
    if (typeof current !== "object" && typeof current !== "function") {
      return undefined;
    }
    parent = current;
    current = (current as Record<string, unknown>)[segment];
  }

  if (typeof current === "function") {
    return (current as (this: unknown) => unknown).call(parent);
  }
  return current;
}

/**
 * The Consent Mode key that governs measurement, and what `consentMode: true`
 * reads. Google's own definition: `analytics_storage` covers storage used for
 * analytics, which is exactly the layer this module gates.
 */
export const DEFAULT_CONSENT_MODE_KEY = "analytics_storage";

/** Where Consent Mode commands are queued on the host page. */
const CONSENT_MODE_QUEUE = "dataLayer";

/** The first element of a Consent Mode entry. */
const CONSENT_MODE_COMMAND = "consent";

/**
 * The verb pushed before the banner is answered, and the only one that takes a
 * `region`.
 */
const CONSENT_MODE_DEFAULT = "default";

/**
 * The two verbs that carry an answer. `default` is pushed before the banner is
 * answered and `update` after it, so both are read and the later one wins.
 * Anything else (`reset`, or a command we have never heard of) is skipped
 * rather than guessed at.
 */
const CONSENT_MODE_VERBS = [CONSENT_MODE_DEFAULT, "update"];

/** `consent`, a verb, and a payload: the three slots a command is read from. */
const CONSENT_MODE_ARITY = 3;

/** The `default` parameter that scopes a command to a list of regions. */
const CONSENT_MODE_REGION = "region";

/**
 * Which Consent Mode key this config asks for, or null when it asks for none.
 * An empty string is "not configured", the same as an empty cookie name.
 */
function consentModeKey(value: boolean | string | undefined): string | null {
  if (value === true) return DEFAULT_CONSENT_MODE_KEY;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * The first three slots of a queue entry, or null when it is not a command.
 *
 * The snippet Google ships is `function gtag(){dataLayer.push(arguments)}`, so
 * most installs queue an `arguments` object rather than a literal array:
 * array-like and indexable, but `Array.isArray` is false on it. Filtering the
 * queue with `Array.isArray` therefore reads an empty queue on the majority of
 * the installs this source exists to serve, and reports "unknown" for all of
 * them.
 *
 * Only three slots are read, so a `length` of any size costs the same, and
 * GTM's ordinary `{event: ...}` pushes have no length and are skipped without
 * being walked.
 */
function consentModeSlots(entry: unknown): unknown[] | null {
  if (Array.isArray(entry)) return [entry[0], entry[1], entry[2]];
  if (!entry || typeof entry !== "object") return null;
  const { length } = entry as { length?: unknown };
  if (typeof length !== "number" || !Number.isFinite(length)) return null;
  if (length < CONSENT_MODE_ARITY) return null;
  const indexed = entry as Record<number, unknown>;
  return [indexed[0], indexed[1], indexed[2]];
}

/**
 * Whether a `default` payload is scoped to regions this module cannot evaluate.
 *
 * `gtag('consent','default',{analytics_storage:'denied',region:['ES','US-CA']})`
 * denies in those places and says nothing about anywhere else. The browser does
 * not know where the visitor is and this module may not ask, so a scoped
 * default is not a global answer. Reading it as one reports a denial to every
 * visitor on earth, and puts "this visitor's site says tracking is denied" on
 * the form overview for forms whose visitors are mostly outside the region.
 * The unscoped `update` a CMP pushes once the banner is answered is what
 * carries the visitor's own choice.
 *
 * `region` scopes a `default` only. An `update` carrying one is honoured
 * globally: that is what Google does with the stray parameter, and it is the
 * reading that keeps a denial rather than dropping it into the permissive
 * default posture.
 *
 * A present but empty list scopes the command to nowhere, so it is not read as
 * a restriction. Skipping on the mere presence of the key would discard an
 * answer over malformed input.
 */
function isRegionScoped(record: Record<string, unknown>): boolean {
  const named = Object.keys(record).find(
    (k) => k.trim().toLowerCase() === CONSENT_MODE_REGION,
  );
  if (typeof named === "undefined") return false;
  const region = record[named];
  if (typeof region === "string") return region.trim().length > 0;
  return Array.isArray(region) && region.length > 0;
}

/**
 * Read one Consent Mode key off the host page's queue.
 *
 * Last write wins: a CMP pushes a `default` and then an `update` once the
 * visitor answers, and a visitor who changes their mind pushes another
 * `update`, so the latest command carrying this key is the answer. A command
 * that names other keys is not an answer about this one and does not reset it.
 *
 * Entries are read through `consentModeSlots`, because the standard snippet
 * queues an `arguments` object rather than an array, and a `default` scoped to
 * a region is skipped by `isRegionScoped` rather than read as a global answer.
 *
 * Anything unrecognised is "unknown", which is deliberately not "granted": a
 * queue we cannot read is not a yes.
 */
function readConsentMode(
  key: string,
  scope: Record<string, unknown> | null,
): TrackingConsentState {
  if (!scope) return "unknown";
  const queue = scope[CONSENT_MODE_QUEUE];
  if (!Array.isArray(queue)) return "unknown";

  const wanted = key.trim().toLowerCase();
  let answer: TrackingConsentState = "unknown";

  for (const entry of queue) {
    const slots = consentModeSlots(entry);
    if (!slots) continue;
    const [command, verb, payload] = slots;
    if (typeof command !== "string") continue;
    if (command.trim().toLowerCase() !== CONSENT_MODE_COMMAND) continue;
    if (typeof verb !== "string") continue;
    const normalizedVerb = verb.trim().toLowerCase();
    if (!CONSENT_MODE_VERBS.includes(normalizedVerb)) continue;
    if (!payload || typeof payload !== "object") continue;

    const record = payload as Record<string, unknown>;
    if (normalizedVerb === CONSENT_MODE_DEFAULT && isRegionScoped(record)) {
      continue;
    }

    const named = Object.keys(record).find(
      (k) => k.trim().toLowerCase() === wanted,
    );
    if (typeof named === "undefined") continue;

    // The customer's own spelling overrides are not consulted: Consent Mode's
    // vocabulary is fixed at "granted" / "denied" by Google, and honouring a
    // remapping of it would only let a customer break a signal they did not
    // configure in the first place.
    answer = interpret(record[named], {});
  }

  return answer;
}

function matchToken(
  value: string,
  config: TrackingConsentConfig,
): TrackingConsentState {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "unknown";

  const extraGranted = (config.grantedValues ?? []).map((v) =>
    v.trim().toLowerCase(),
  );
  const extraDenied = (config.deniedValues ?? []).map((v) =>
    v.trim().toLowerCase(),
  );
  if (extraGranted.includes(normalized)) return "granted";
  if (extraDenied.includes(normalized)) return "denied";
  if (GRANTED_TOKENS.includes(normalized)) return "granted";
  if (DENIED_TOKENS.includes(normalized)) return "denied";
  return "unknown";
}

/**
 * Turn whatever the source produced into a state. Recognises booleans, the
 * spellings above, JSON, the unquoted `{key:value}` shape consent cookies use,
 * and a list of granted category ids. Anything it does not recognise is
 * "unknown", which is deliberately not "granted": a value we cannot read is
 * not a yes.
 */
function interpret(
  value: unknown,
  config: TrackingConsentConfig,
  depth = 0,
): TrackingConsentState {
  if (depth > MAX_DEPTH) return "unknown";
  if (value === null || typeof value === "undefined") return "unknown";

  if (typeof value === "boolean") return value ? "granted" : "denied";

  if (typeof value === "number") {
    if (value === 1) return "granted";
    if (value === 0) return "denied";
    return "unknown";
  }

  if (typeof value === "function") {
    return interpret((value as () => unknown)(), config, depth + 1);
  }

  if (Array.isArray(value)) {
    if (!config.category) return "unknown";
    const wanted = config.category.trim().toLowerCase();
    const has = value.some(
      (entry) => String(entry).trim().toLowerCase() === wanted,
    );
    return has ? "granted" : "denied";
  }

  if (typeof value === "object") {
    if (!config.category) return "unknown";
    const record = value as Record<string, unknown>;
    const key = Object.keys(record).find(
      (k) => k.trim().toLowerCase() === config.category!.trim().toLowerCase(),
    );
    if (typeof key === "undefined") return "unknown";
    return interpret(record[key], config, depth + 1);
  }

  if (typeof value !== "string") return "unknown";

  const direct = matchToken(value, config);
  if (direct !== "unknown") return direct;

  const trimmed = value.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return interpret(JSON.parse(trimmed), config, depth + 1);
    } catch {
      /* not JSON; the pair scan below still has a chance */
    }
  }

  if (config.category) {
    // `{necessary:true,ads:false}` and `ads=false; other=true` both appear in
    // real consent cookies, and neither parses as JSON.
    const pattern = new RegExp(
      `["']?${escapeForRegExp(config.category)}["']?\\s*[:=]\\s*["']?([^,;}"']+)`,
      "i",
    );
    const found = pattern.exec(trimmed);
    if (found) return matchToken(found[1], config);
  }

  return "unknown";
}

/**
 * Whether the customer pointed us at anywhere to read an answer from.
 *
 * Exported because three callers need the same answer and had three copies of
 * it: this module's resolver, its dashboard description, and `hasConsentSource`
 * in popupTriggers.ts, which decides whether a mid-session revoke watch runs. A
 * fourth source added to two of the three is a signal the engine reads at arm
 * time and then stops watching.
 *
 * `gpc` is deliberately not a source here. It is the browser's own signal
 * rather than something the customer configured, it can only ever deny, and
 * there is nothing to poll for a change of answer.
 */
export function hasTrackingConsentSource(
  config: TrackingConsentConfig | undefined | null,
): boolean {
  if (!config || typeof config !== "object") return false;
  return (
    Boolean(config.windowPath) ||
    consentModeKey(config.consentMode) !== null ||
    Boolean(config.cookie) ||
    Boolean(config.storageKey)
  );
}

/**
 * Pull the consent config out of a form's stored `settings`, wherever it lives.
 *
 * Two locations, because task 0.1 of
 * docs/consent-signal-and-ip-minimisation_plan.md promoted the field out of
 * `settings.popup` and up to `settings`. The nested one existed only for forms
 * that are popups, and LiveForm renders every form: hosted pages, inline embeds
 * and popups alike, so a plain embedded form had nowhere to put a consent
 * signal. The promoted location wins; the nested one is read indefinitely so no
 * form loses a setting and there is no migration to write.
 *
 * Lives here rather than in `@/lib/utils` because both renderers need the same
 * answer and one of them is `kiroku-embed.ts`, which ships in the standalone
 * bundle and must stay free of `@/lib`. Two copies of a fallback rule is how
 * two renderers come to disagree about whether a visitor consented.
 *
 * The argument is `unknown` on purpose: `settings` is JSON off an API response,
 * so every level of it can be null, a string, or a shape nobody writes any
 * more. Anything unreadable yields `{}`, which names no source and resolves to
 * `DEFAULT_TRACKING_POSTURE`, exactly as an unconfigured form does.
 */
export function trackingConsentFromSettings(
  settings: unknown,
): TrackingConsentConfig {
  const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;

  const root = asRecord(settings);
  if (!root) return {};
  const promoted = asRecord(root.trackingConsent);
  if (promoted) return promoted as TrackingConsentConfig;
  const nested = asRecord(asRecord(root.popup)?.trackingConsent);
  return (nested as TrackingConsentConfig | null) ?? {};
}

function ambientCookieString(): string | null {
  try {
    return typeof document === "undefined" ? null : document.cookie;
  } catch {
    return null;
  }
}

function ambientStorage(): ConsentStorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null; // storage can throw on access alone when it is blocked
  }
}

function ambientScope(): Record<string, unknown> | null {
  try {
    return typeof window === "undefined"
      ? null
      : (window as unknown as Record<string, unknown>);
  } catch {
    return null;
  }
}

/** Read one source, swallowing anything it throws. */
function readSource(read: () => unknown): unknown {
  try {
    return read();
  } catch {
    return undefined; // a consent tool mid-initialisation must not break the embed
  }
}

/**
 * Resolve the posture: what the signal said, which source said it, whether the
 * analytics layer may run, and a sentence a form owner can read.
 *
 * Sources are consulted in order (window path, Consent Mode, cookie, storage)
 * and the first one that produces a state it recognises wins. A configured source that is
 * absent or unreadable does not stop the next one from answering.
 */
export function resolveTrackingConsent(
  config: TrackingConsentConfig = {},
  environment: TrackingConsentEnvironment = {},
): TrackingConsentResolution {
  // Both arguments are JSON that reached us through an API response:
  // `settings.popup.trackingConsent` is null on every form nobody configured,
  // and can be anything at all on one written by an older settings shape. A
  // default parameter only covers `undefined`, so `resolveTrackingConsent(null)`
  // threw a TypeError. `describeTrackingConsentPosture` has failed safe on this
  // since it was written; this one runs on the visitor's page, where the same
  // throw takes the popup engine down with it and the module header promises it
  // cannot.
  const safe: TrackingConsentConfig =
    config && typeof config === "object" ? config : {};
  const env: TrackingConsentEnvironment =
    environment && typeof environment === "object" ? environment : {};
  const configured = hasTrackingConsentSource(safe);

  const scope = () => (env.scope === undefined ? ambientScope() : env.scope);

  const attempts: Array<{
    source: TrackingConsentSource;
    read: () => unknown;
    /** How to turn the raw value into a state; GPC inverts, see interpretGpc. */
    interpretValue: (raw: unknown) => TrackingConsentState;
    /** Whether a denial from this attempt came from the browser, not the site. */
    fromBrowser?: boolean;
  }> = [];

  // First, because it can only ever deny and that denial outranks a banner
  // that granted.
  if (safe.gpc) {
    attempts.push({
      source: "window",
      read: () => readWindowPath(GPC_WINDOW_PATH, scope()),
      interpretValue: interpretGpc,
      fromBrowser: true,
    });
  }

  if (safe.windowPath) {
    const path = safe.windowPath;
    const gpcPath = isGpcPath(path);
    attempts.push({
      source: "window",
      read: () => readWindowPath(path, scope()),
      interpretValue: gpcPath ? interpretGpc : (raw) => interpret(raw, safe),
      fromBrowser: gpcPath,
    });
  }
  const consentMode = consentModeKey(safe.consentMode);
  if (consentMode) {
    attempts.push({
      source: "consentMode",
      read: () => readConsentMode(consentMode, scope()),
      // `readConsentMode` already returns a state, so this validates rather
      // than interprets: the queue's vocabulary is Google's, not the
      // customer's. It has to validate and not cast, because `readSource`
      // turns a throw into `undefined`, and an `undefined` cast to a state is
      // neither "unknown" nor a value the loop below stops on. That shipped
      // for one commit and made a consent tool that throws mid-initialisation
      // resolve to a denial the dashboard blamed on the customer's own site.
      interpretValue: (raw) =>
        raw === "granted" || raw === "denied" ? raw : "unknown",
    });
  }

  if (safe.cookie) {
    const name = safe.cookie;
    attempts.push({
      source: "cookie",
      read: () =>
        readCookie(
          name,
          env.cookieString === undefined
            ? ambientCookieString()
            : env.cookieString,
        ),
      interpretValue: (raw) => interpret(raw, safe),
    });
  }
  if (safe.storageKey) {
    const key = safe.storageKey;
    attempts.push({
      source: "storage",
      read: () => {
        const storage =
          env.storage === undefined ? ambientStorage() : env.storage;
        return storage ? storage.getItem(key) : undefined;
      },
      interpretValue: (raw) => interpret(raw, safe),
    });
  }

  for (const attempt of attempts) {
    const raw = readSource(attempt.read);
    let state: TrackingConsentState = "unknown";
    try {
      state = attempt.interpretValue(raw);
    } catch {
      state = "unknown";
    }
    if (state === "unknown") continue;
    return {
      state,
      source: attempt.source,
      allowed: state === "granted",
      reason:
        state === "granted"
          ? TRACKING_CONSENT_REASONS.granted
          : attempt.fromBrowser
            ? TRACKING_CONSENT_REASONS.deniedByBrowser
            : TRACKING_CONSENT_REASONS.deniedBySite,
    };
  }

  const allowed = DEFAULT_TRACKING_POSTURE === "allow";
  return {
    state: "unknown",
    source: null,
    allowed,
    reason: configured
      ? allowed
        ? TRACKING_CONSENT_REASONS.unreadableCounted
        : TRACKING_CONSENT_REASONS.unreadableNotCounted
      : allowed
        ? TRACKING_CONSENT_REASONS.noSignalCounted
        : TRACKING_CONSENT_REASONS.noSignalNotCounted,
  };
}

/**
 * What a stored config means for the form's owner, without a browser.
 *
 * `resolveTrackingConsent` answers for one visitor on one page load, which is
 * the only place the real answer exists: the signal lives in *their* browser on
 * the *customer's* site, and a denial sends us nothing, so no count of
 * suppressions can reach our database without making the transmission the
 * denial told us not to make. What the dashboard can say is what this form is
 * configured to do and which answers stop a count, which is what task 5.3 of
 * docs/consent-management_plan.md asks for.
 *
 * Every sentence returned is one `resolveTrackingConsent` would itself produce
 * for that config, so the panel cannot describe behaviour the engine does not
 * have.
 */
export interface TrackingConsentPosture {
  /** Whether the form names a signal to read. False on most sites. */
  configured: boolean;
  /** Whether any answer can stop a count. False when nothing is named. */
  canSuppress: boolean;
  /** What happens on the ordinary path, granted or nothing to read. */
  counted: string;
  /** What a denial does, or null when nothing can answer no. */
  suppressed: string | null;
  /** What a mid-visit withdrawal does, or null when nothing is watched. */
  withdrawn: string | null;
  /** What an unreadable named signal does, or null when none is named. */
  unreadable: string | null;
  /**
   * Which setting decides, named the way the owner typed it, so a panel can say
   * *this* cookie rather than "your consent signal". Null when nothing is
   * named. The order matches the order `resolveTrackingConsent` consults its
   * sources in, so a config that somehow names several reports the one that
   * would actually answer. Consent Mode is the one source the owner types no
   * name for, so it reports the key that is read (`analytics_storage` unless
   * another was chosen).
   */
  signal: { source: TrackingConsentSource; name: string } | null;
}

export function describeTrackingConsentPosture(
  config: TrackingConsentConfig = {},
): TrackingConsentPosture {
  // `settings.popup.trackingConsent` is stored as JSON and reaches the panel
  // through an API response, so it can arrive as null or as something that is
  // not an object at all. Failing safe here means describing the documented
  // default posture rather than throwing inside a dashboard render: the module
  // header promises nothing here throws, and a blank overview page would tell
  // the owner even less than the wrong sentence would.
  const safe: TrackingConsentConfig =
    config && typeof config === "object" ? config : {};
  // The same predicate the resolver and the engine use, rather than a third
  // copy of the source list: a source added to one and not the others is a
  // signal the visitor's page reads while the owner's dashboard says there is
  // nothing set up.
  const configured = hasTrackingConsentSource(safe);
  const consentMode = consentModeKey(safe.consentMode);
  const allowed = DEFAULT_TRACKING_POSTURE === "allow";

  // GPC is consulted first and can only ever deny, so it makes a form
  // suppressible on its own. A `windowPath` naming the GPC property carries the
  // same polarity, which is why the denial sentence follows the source rather
  // than the flag.
  const gpcOnly =
    Boolean(safe.gpc) &&
    !safe.cookie &&
    !safe.storageKey &&
    !consentMode &&
    (!safe.windowPath || isGpcPath(safe.windowPath));

  if (!configured && !safe.gpc) {
    return {
      configured: false,
      canSuppress: false,
      counted: allowed
        ? TRACKING_CONSENT_REASONS.noSignalCounted
        : TRACKING_CONSENT_REASONS.noSignalNotCounted,
      suppressed: null,
      withdrawn: null,
      unreadable: null,
      signal: null,
    };
  }

  return {
    configured,
    canSuppress: true,
    counted: TRACKING_CONSENT_REASONS.granted,
    suppressed: gpcOnly
      ? TRACKING_CONSENT_REASONS.deniedByBrowser
      : TRACKING_CONSENT_REASONS.deniedBySite,
    // Only a configured source is polled for a change of answer; see
    // `startConsentWatch` in popupTriggers.ts, which needs a named source.
    withdrawn: configured ? TRACKING_CONSENT_REASONS.revoked : null,
    unreadable: configured
      ? allowed
        ? TRACKING_CONSENT_REASONS.unreadableCounted
        : TRACKING_CONSENT_REASONS.unreadableNotCounted
      : null,
    signal: safe.windowPath
      ? { source: "window", name: safe.windowPath }
      : consentMode
        ? { source: "consentMode", name: consentMode }
        : safe.cookie
          ? { source: "cookie", name: safe.cookie }
          : safe.storageKey
            ? { source: "storage", name: safe.storageKey }
            : null,
  };
}

/** The state alone, for a caller that does not need the reason. */
export function readTrackingConsent(
  config: TrackingConsentConfig = {},
  environment: TrackingConsentEnvironment = {},
): TrackingConsentState {
  return resolveTrackingConsent(config, environment).state;
}

/**
 * Whether the analytics layer may run. The one question the popup engine asks:
 * never consulted for opening the modal or for the frequency cap.
 */
export function trackingAllowed(
  config: TrackingConsentConfig = {},
  environment: TrackingConsentEnvironment = {},
): boolean {
  return resolveTrackingConsent(config, environment).allowed;
}
